import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { newId } from '@/lib/id';

/**
 * נהלים וחתימות (PRD 5.10.1).
 *
 * החתימה נקשרת לגרסת הנוהל, ולכן עדכון תוכן מחזיר את הנוהל לרשימת
 * מה שצריך לחתום. הגרסה נקבעת בשרת, גם בעדכון וגם בחתימה.
 */

/** קטגוריה אחת של נוהל, כפי שהיא חוזרת מ document_list. */
export type DocCategory = { id: string; name: string; sort_order: number };

export type Document = {
  id: string;
  title: string;
  body: string | null;
  file_path: string | null;
  version: number;
  requires_signature: boolean;
  is_published: boolean;
  updated_at: string;
  /**
   * כמה קטגוריות ולא אחת (מיגרציה 083).
   *
   * נוהל בטיחות אש הוא גם של החנות וגם של המחסן, ושדה יחיד הכריח
   * לשכפל את הנוהל — ואז שתי רשימות חתימות על אותו נוסח.
   */
  categories: DocCategory[];
};

export type PendingSignature = {
  document_id: string;
  title: string;
  version: number;
  updated_at: string;
};

export type SignatureReport = {
  version: number;
  total: number;
  signed: number;
  employees: {
    employee_id: string;
    name: string;
    branch: string | null;
    signed_at: string | null;
  }[];
};

export const DOCS_KEY = ['documents'] as const;

/**
 * הנהלים, עם הקטגוריות שלהם.
 *
 * `all` הוא מצב הניהול: גם מה שעוד לא פורסם. בלעדיו נוהל שבוטל
 * פרסומו נעלם גם ממסך הניהול ולא היה אפשר לפרסם אותו מחדש.
 */
export function useDocuments(all = false) {
  return useQuery({
    queryKey: [...DOCS_KEY, all ? 'all' : 'published'],
    queryFn: async (): Promise<Document[]> => {
      const { data, error } = await supabase.rpc('document_list', { p_all: all });
      if (error) throw error;
      return (data as unknown as Document[]) ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** החתימה האחרונה של העובד על נוהל, כדי לדעת אם היא על הגרסה הזו. */
export function useMySignature(documentId: string) {
  return useQuery({
    queryKey: [...DOCS_KEY, 'signature', documentId],
    enabled: Boolean(documentId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('document_signatures')
        .select('document_version, signed_at')
        .eq('document_id', documentId)
        .order('document_version', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

/** מה שמחכה לחתימה, למסך הבית ולרשימה. */
export function usePendingSignatures() {
  return useQuery({
    queryKey: [...DOCS_KEY, 'to-sign'],
    queryFn: async (): Promise<PendingSignature[]> => {
      const { data, error } = await supabase.rpc('documents_to_sign');
      if (error) throw error;
      return (data as PendingSignature[] | null) ?? [];
    },
  });
}

export function useSignatureReport(documentId: string | null) {
  return useQuery({
    queryKey: [...DOCS_KEY, 'report', documentId],
    enabled: Boolean(documentId),
    queryFn: async (): Promise<SignatureReport | null> => {
      const { data, error } = await supabase.rpc('document_signature_report', {
        p_document: documentId as string,
      });
      if (error) throw error;
      return data as SignatureReport | null;
    },
  });
}

export function useSignDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { documentId: string; signaturePath: string | null }) => {
      const { error } = await supabase.rpc('sign_document', {
        p_document: input.documentId,
        p_signature_path: input.signaturePath ?? undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DOCS_KEY });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** העלאת החתימה לבדל signatures. הנתיב מתחיל במזהה העובד. */
export async function uploadSignature(employeeId: string, blob: Blob): Promise<string> {
  const path = `${employeeId}/${newId()}.png`;
  const { error } = await supabase.storage.from('signatures').upload(path, blob, {
    contentType: 'image/png',
    upsert: false,
  });
  if (error) throw error;
  return path;
}

/** קובץ מצורף לנוהל, אם יש. */
export function useDocumentFile(path: string | null | undefined) {
  return useQuery({
    queryKey: ['document-file', path],
    enabled: Boolean(path),
    staleTime: 50 * 60 * 1000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase.storage
        .from('learning')
        .createSignedUrl(path as string, 3600);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

/** חיפוש מקומי בנהלים: כותרת, קטגוריה ותוכן. */
export function searchDocuments(documents: Document[], term: string): Document[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return documents;
  return documents.filter((document) =>
    [document.title, document.body, ...document.categories.map((category) => category.name)]
      .filter(Boolean)
      .some((field) => (field as string).toLowerCase().includes(needle)),
  );
}

export type DocGroup = { id: string | null; name: string; documents: Document[] };

/**
 * הנהלים מחולקים לקטגוריות, לפי הסדר שנקבע במסך הניהול.
 *
 * נוהל שמשויך לשתיים מופיע בשתיהן. זה מכוון: הוא באמת חל על
 * שתיהן, ועובד שמחפש אותו במחסן לא צריך לדעת שהוא "בעצם" של
 * החנות. מה שבלי קטגוריה נאסף לבלוק אחרון, כדי שלא ייעלם.
 */
export function groupByCategory(documents: Document[]): DocGroup[] {
  const groups = new Map<string, DocGroup & { sort: number }>();
  const loose: Document[] = [];

  for (const document of documents) {
    if (document.categories.length === 0) {
      loose.push(document);
      continue;
    }
    for (const category of document.categories) {
      const group = groups.get(category.id) ?? {
        id: category.id,
        name: category.name,
        sort: category.sort_order,
        documents: [],
      };
      group.documents.push(document);
      groups.set(category.id, group);
    }
  }

  const ordered = [...groups.values()]
    .sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'he'))
    .map(({ id, name, documents: rows }) => ({ id, name, documents: rows }));

  if (loose.length > 0) ordered.push({ id: null, name: 'בלי קטגוריה', documents: loose });
  return ordered;
}
